import { FC, useEffect, useMemo, useState } from "react";
import {
  MRT_ColumnDef,
  MRT_PaginationState,
  MantineReactTable,
  useMantineReactTable,
} from "mantine-react-table";
import { toast } from "react-toastify";
import { InputSelect } from "@funded-here-interface/common";
import useGetExternalAccounts from "../../../hooks/useGetExternalAccounts";
import useGetPaymentsListing from "../../../hooks/useGetPaymentsListing";
import { PaymentsListing as PaymentListing } from "../../../services/getPaymentsListing";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import useDownloadPaymentsListingCSV from "../../../hooks/useDownloadPaymentsListingCSV";
import { downloadFile } from "@funded-here-interface/common/src/Utils/file";
import { FileType } from "@funded-here-interface/common/src/constant/enum";
import { IconDownload } from "@tabler/icons-react";
import { Box, Button } from "@mantine/core";
import { convertDate } from "@funded-here-interface/common/src/Utils/date";
import { DatePickerInput, DatesRangeValue } from "@mantine/dates";

const PaymentsListing: FC = () => {
  const token = useSelector((state: RootState) => state.auth.token);
  //store pagination state in your own state
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const [externalAccountList, setExternalAccountList] = useState<any>([]);

  const [externalAccount, setExternalAccount] = useState("");

  const [dataLoading, setDataLoading] = useState(false);

  const [refreshKey, setRefreshKey] = useState(0);

  // Set default date range to last 1 month
  const [dateRange, setDateRange] = useState<DatesRangeValue>(() => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setMonth(startDate.getMonth() - 1);
    return [startDate, endDate];
  });

  const { data: fetchExternalAccount } = useGetExternalAccounts(token);

  // Convert date range to ISO strings for API only if both dates are selected
  // Set end date to end of day (23:59:59.999) to include all records for that day
  const startDate =
    dateRange[0] && dateRange[1] ? dateRange[0].toISOString() : undefined;
  const endDate =
    dateRange[0] && dateRange[1]
      ? (() => {
          const endOfDay = new Date(dateRange[1]);
          endOfDay.setHours(23, 59, 59, 999);
          return endOfDay.toISOString();
        })()
      : undefined;

  const { data, error, isLoading } = useGetPaymentsListing(
    token,
    externalAccount,
    pagination,
    refreshKey,
    startDate,
    endDate
  );

  const [isExportLoading, setIsExportLoading] = useState(false);

  const useDownloadPaymentsListingCSVMutation = useDownloadPaymentsListingCSV();

  if (error) {
    toast.error((error as Error).message);
  }

  useEffect(() => {
    if (fetchExternalAccount && fetchExternalAccount.length > 0) {
      setExternalAccountList(
        fetchExternalAccount.map((item: any) => ({
          label: item.ref,
          value: item.accountId,
        }))
      );
    }
  }, [fetchExternalAccount]);

  const handlePaginationChange = (
    updater:
      | MRT_PaginationState
      | ((old: MRT_PaginationState) => MRT_PaginationState)
  ) => {
    const newPagination =
      typeof updater === "function" ? updater(pagination) : updater;

    if (
      newPagination.pageIndex !== pagination.pageIndex ||
      newPagination.pageSize !== pagination.pageSize
    ) {
      setDataLoading(true);
      setPagination(newPagination);
    }
  };

  useEffect(() => {
    setDataLoading(false);
  }, [data]);

  const totalRowCount = data?.totalRowCount ?? 0;
  const totalPages = data?.totalPages ?? 0;

  const handleExportData = () => {
    toast.info("Please wait a moment for system to export your data");
    setIsExportLoading(true);
    useDownloadPaymentsListingCSVMutation.mutate(
      {
        token,
        externalAccount,
        totalPages,
        totalRowCount,
        startDate,
        endDate,
      },
      {
        onSuccess: (data: Blob) => {
          setIsExportLoading(false);
          downloadFile(data, "PaymentsListing", FileType.CSV);
        },
        onError: (e) => {
          setIsExportLoading(false);
          toast.error((e as Error).message);
        },
      }
    );
  };

  const columns = useMemo<MRT_ColumnDef<PaymentListing>[]>(
    () => [
      {
        id: "general",
        header: "",
        columns: [
          {
            accessorKey: "simplePaymentId",
            header: "Simple Payment ID",
          },
          {
            accessorKey: "totalAmount",
            header: "Total Amount",
          },
          {
            accessorKey: "status",
            header: "Status",
          },
          {
            accessorKey: "buyAmount",
            header: "Buy Amount",
          },
          {
            accessorKey: "buyCurrency",
            header: "Buy Currency",
          },
          {
            accessorKey: "sellAmount",
            header: "Sell Amount",
          },
          {
            accessorKey: "sellCurrency",
            header: "Sell Currency",
          },
          {
            accessorKey: "currencyPair",
            header: "Currency Pair",
          },
          {
            accessorKey: "rate",
            header: "Rate",
          },
          {
            accessorKey: "paymentFee",
            header: "Payment Fee",
          },
          {
            accessorKey: "vatValue",
            header: "Vat Value",
          },
          {
            accessorKey: "vatBaseValue",
            header: "Vat Base Value",
          },
          {
            accessorKey: "totalFee",
            header: "Total Fee",
          },
          {
            id: "createdAt",
            accessorKey: "createdAt",
            header: "Created Date",
            Cell: ({ cell }) => {
              const value = cell.getValue<string>();
              return value ? <p>{convertDate(value)}</p> : <div>&#8210;</div>;
            },
          },
          {
            accessorKey: "completedAt",
            header: "Completed Date",
            Cell: ({ cell }) => {
              const value = cell.getValue<string>();
              return value ? <p>{convertDate(value)}</p> : <div>&#8210;</div>;
            },
          },
          {
            accessorKey: "reference",
            header: "Reference",
          },
          {
            accessorKey: "paymentReference",
            header: "Payment Reference",
          },
          {
            accessorKey: "fundingSource",
            header: "Funding Source",
          },
          {
            accessorKey: "comment",
            header: "Comment",
          },
          {
            accessorKey: "paymentChannel",
            header: "Payment Channel",
          },
          {
            accessorKey: "bankCharges",
            header: "Bank Charges",
          },
          {
            accessorKey: "purposeOfTransfer",
            header: "Purpose of Transfer",
          },
          {
            accessorKey: "fundingReference",
            header: "Funding Reference",
          },
          {
            accessorKey: "simplePaymentPartnerAccountId",
            header: "Simple Payment Partner Account Id",
            Cell: ({ renderedCellValue }) =>
              renderedCellValue ? (
                <span>{renderedCellValue}</span>
              ) : (
                <div>&#8210;</div>
              ),
          },
          {
            accessorKey: "fixedSide",
            header: "Fixed Side",
          },
          {
            accessorKey: "settlementId",
            header: "Settlement ID",
            Cell: ({ renderedCellValue }) =>
              renderedCellValue ? (
                <span>{renderedCellValue}</span>
              ) : (
                <div>&#8210;</div>
              ),
          },
          {
            accessorKey: "fundingCutoffTime",
            header: "Funding Cut Off Time",
            Cell: ({ cell }) => {
              const value = cell.getValue<string>();
              return value ? <p>{convertDate(value)}</p> : <div>&#8210;</div>;
            },
          },
        ],
      },
      {
        id: "beneficiary",
        header: "Beneficiary",
        mantineTableHeadCellProps: {
          sx: {
            backgroundColor: "#D9D9D9",
          },
        },
        columns: [
          {
            accessorFn: (row) => row.beneficiary?.entityType,
            header: "Entity Type",
          },
          {
            accessorFn: (row) => row.beneficiary?.nickname,
            header: "Nick Name",
          },
          {
            accessorFn: (row) => row.beneficiary?.companyName,
            header: "Company Name",
            Cell: ({ renderedCellValue }) =>
              renderedCellValue ? (
                <span>{renderedCellValue}</span>
              ) : (
                <div>&#8210;</div>
              ),
          },
          {
            accessorFn: (row) => row.beneficiary?.address,
            header: "Address",
          },
          {
            accessorFn: (row) => row.beneficiary?.city,
            header: "City",
          },
          {
            id: "beneficiaryCountry",
            accessorFn: (row) => row.beneficiary?.country,
            header: "Country",
          },
          {
            accessorFn: (row) => row.beneficiary?.marketMakerRef,
            header: "Market Maker Ref",
          },
          {
            id: "beneficiaryCreatedAt",
            accessorFn: (row) => row.beneficiary?.createdAt,
            header: "Created Date",
            Cell: ({ cell }) => {
              const value = cell.getValue<string>();
              return value ? <p>{convertDate(value)}</p> : <div>&#8210;</div>;
            },
          },
          {
            accessorFn: (row) => row.beneficiary?.type,
            header: "Type",
          },
          {
            id: "bankAccount",
            header: "Bank Account",
            mantineTableHeadCellProps: {
              sx: {
                backgroundColor: "#FFB6C1",
              },
            },
            columns: [
              {
                accessorFn: (row) => row.beneficiary?.bankAccount?.currency,
                header: "Currency",
              },
              {
                accessorFn: (row) => row.beneficiary?.bankAccount?.bicSwift,
                header: "Bic Swift",
              },
              {
                accessorFn: (row) => row.beneficiary?.bankAccount?.bankName,
                header: "Bank Name",
              },
              {
                id: "beneficiaryBankAccountCountry",
                accessorFn: (row) => row.beneficiary?.bankAccount?.country,
                header: "Country",
              },
              {
                accessorFn: (row) =>
                  row.beneficiary?.bankAccount?.accountNumber,
                header: "Account Number",
              },
              {
                accessorFn: (row) =>
                  row.beneficiary?.bankAccount?.bankAccountHolderName,
                header: "Bank Account Holder Name",
              },
              {
                id: "beneficiaryBankAccountCreatedAt",
                accessorFn: (row) => row.beneficiary?.bankAccount?.createdAt,
                header: "Created Date",
                Cell: ({ cell }) => {
                  const value = cell.getValue<string>();
                  return value ? (
                    <p>{convertDate(value)}</p>
                  ) : (
                    <div>&#8210;</div>
                  );
                },
              },
            ],
          },
        ],
      },
    ],
    []
  );

  const table = useMantineReactTable<PaymentListing>({
    enableColumnActions: false,
    enableColumnFilters: false,
    enableDensityToggle: false,
    paginationDisplayMode: "pages",
    state: { pagination, isLoading: isLoading || dataLoading }, //pass the pagination and isLoading state to the table
    columns,
    data: data?.data ?? [],
    manualPagination: true,
    onPaginationChange: handlePaginationChange, //hoist pagination state to your state when it changes internally
    rowCount: totalRowCount, //pass the total row count to the table
    initialState: { density: "xs" },
    mantineTableProps: {
      withColumnBorders: true,
    },
    mantineTableHeadCellProps: {
      style: {
        padding: "0.5rem 1.0rem",
        color: "#23262a",
      },
    },
    mantineTableBodyCellProps: {
      style: {
        padding: "0.5rem",
      },
    },
    renderTopToolbarCustomActions: ({ table }) => (
      <Box
        sx={{
          display: "flex",
          gap: "16px",
          padding: "8px",
          flexWrap: "wrap",
        }}
      >
        <Button
          color="lightblue"
          //export all data that is currently in the table (ignore pagination, sorting, filtering, etc.)
          onClick={handleExportData}
          leftIcon={<IconDownload />}
          variant="filled"
          loading={isExportLoading}
        >
          Export All Data
        </Button>
      </Box>
    ),
  });

  return (
    <div>
      <div
        style={{
          display: "flex",
          gap: "16px",
          marginBottom: "16px",
          alignItems: "flex-start",
        }}
      >
        <div style={{ flex: 1 }}>
          <InputSelect
            onChange={(value: string) => {
              setExternalAccount(value);
              setRefreshKey((prev) => prev + 1);
              setDataLoading(true);
            }}
            labelText="External Account"
            id="externalAccount"
            value={externalAccount}
            data={externalAccountList}
          />
        </div>
        <div style={{ flex: 1 }}>
          <DatePickerInput
            type="range"
            label="Created Date Range"
            value={dateRange}
            onChange={(value) => {
              setDateRange(value);
              // Only trigger refresh if both dates are selected
              if (value[0] && value[1]) {
                setRefreshKey((prev) => prev + 1);
                setDataLoading(true);
                setPagination({ pageIndex: 0, pageSize: pagination.pageSize });
              }
            }}
            clearable
            allowSingleDateInRange={true}
            valueFormat="YYYY-MM-DD"
            styles={{
              label: {
                marginBottom: "5px",
              },
              input: {
                paddingLeft: "12px",
                height: "41px",
              },
            }}
          />
        </div>
      </div>
      <MantineReactTable table={table} />
    </div>
  );
};

export default PaymentsListing;
